'use client';

import Link from 'next/link';
import { formatEventDate, toSlug } from '@bip/shared';
import type { Event } from '@bip/api-client';
import { useCurrency } from '../../_components/CurrencyProvider';
import { IMG } from '../../imgPrefix';

interface EventCardProps {
  event: Event;
}

export function EventCard({ event }: EventCardProps) {
  const { formatPrice } = useCurrency();

  const eventTitle = event.name;
  const eventSlug = toSlug(eventTitle, event.id);

  // Spec: "Friday, 8 May 2026 – 21:00" — `formatEventDate` produces the
  // en-GB date portion; the time comes through verbatim from SE365 in 24h
  // HH:mm. The separator is an en-dash (U+2013), not a hyphen.
  const dateDisplay = event.dateOfEvent
    ? `${formatEventDate(event.dateOfEvent)} – ${event.timeOfEvent || ''}`
    : '';

  const venueName = event.venue?.name || '';
  // Per spec: Location = City, State, Country. `state` isn't declared
  // on the `Event` type today (SE365 doesn't always ship it); read
  // defensively so if a future payload adds it — either at the top
  // level or nested under `city` — it surfaces without a type
  // change. Empty parts drop out via `filter(Boolean)`.
  const cityName = event.city?.name || '';
  const stateName =
    (event as any)?.state || (event.city as any)?.state || (event as any)?.stateName || '';
  const countryName = event.country?.name || '';
  const locationDisplay = [cityName, stateName, countryName].filter(Boolean).join(', ');

  return (
    <div className=" col-12 col-md-6 col-lg-4 mb-4 pb-4">
      <div className="Recommended-bx cutout-shape">
        <div className="cutout-inner">
          <div className="p-4 h-100">
            <div className="card-display justify-content-between">
              <div className="pb-3">
                {/* <div className="category-text">{event.eventType?.name ?? 'COncert'}</div> */}
              </div>
            </div>

            <div className="heading">
              <h3>
                <Link
                  href={`/concerts/${eventSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-black"
                >
                  {eventTitle}
                </Link>
              </h3>
            </div>

            {/* Tournament row removed per concerts spec — not
                surfaced on the listing card. */}

            {/* Venue — dedicated row with its own icon per spec.
                Previously venue + city were joined into a single
                `location` line; the venue name deserves its own
                emphasis so we split them. */}
            {venueName && (
              <div className="pb-2 sports-text">
                <img src={`${IMG}/image/location.svg`} alt="" className="pr-2" />
                {venueName}, {locationDisplay}
              </div>
            )}

            {/* Location — City, State, Country with pin icon. */}
            {locationDisplay && <div className="pb-2 ml-4 sports-text"></div>}

            {/* Date & Time */}
            {dateDisplay && (
              <div className="pb-2 sports-text">
                <img src={`${IMG}/image/calendar-01.svg`} alt="" className="pr-2" />
                {dateDisplay}
              </div>
            )}

            {/* Price + date/time not final indicators */}
            <div className="pt-2 d-flex align-items-center justify-content-between">
              <div>
                <h3 className="text-primary owners-font h5 mb-0">
                  From{' '}
                  {formatPrice(
                    event.minTicketPrice?.price ?? 0,
                    event.minTicketPrice?.currency ?? 'GBP',
                  )}
                </h3>
              </div>
              {event.finalDate === false && (
                <span className="not-final-indicator">
                  <span
                    className="info-tooltip"
                    data-tooltip={event.dateTimeBoundaries || 'Date not final'}
                  >
                    <i className="fa-solid fa-circle-info"></i>
                  </span>
                  <span className="not-final-badge ml-1">Date not final</span>
                </span>
              )}
              {event.finalDate !== false && event.finalTime === false && (
                <span className="not-final-indicator">
                  <span
                    className="info-tooltip"
                    data-tooltip={event.dateTimeBoundaries || 'Time not final'}
                  >
                    <i className="fa-solid fa-circle-info"></i>
                  </span>
                  <span className="not-final-badge ml-1">Time not final</span>
                </span>
              )}
            </div>

            {/* Book Now — opens the event detail page in a new tab so the
                customer's listing page stays available for comparison. */}
            <div className="pt-2 d-flex align-items-center justify-content-between Recommended-web-pad">
              <Link href={`/concerts/${eventSlug}`} target="_blank" rel="noopener noreferrer">
                <span className="btn btn-primary btn-sm getPropertyDetail list-book-now-btn">
                  Book Now
                </span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
